import { useEffect, useState } from 'react'
import { ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

/** Public status page (I6): no auth, auto-refresh. Served at /status. */

interface SourceRow {
  source_id: string
  country_code: string | null
  enabled: boolean
  freshness: string
  last_sync_at: string | null
  record_count: number
}

interface StatusPayload {
  api: string
  database: string
  databaseLatencyMs: number
  syncQueue: Record<string, number>
  sources: SourceRow[]
}

function Badge({ state }: { state: string }) {
  const style =
    state === 'UP' || state === 'FRESH' || state === 'ACTIVE'
      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
      : state === 'STALE' || state === 'UNKNOWN'
        ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
        : 'bg-red-500/10 text-red-600 dark:text-red-400'
  return <span className={clsx('badge px-3 py-1 font-semibold', style)}>{state}</span>
}

export default function StatusPage() {
  const { t } = useTranslation()
  const [status, setStatus] = useState<StatusPayload | null>(null)

  useEffect(() => {
    const load = () => {
      fetch('/v1/meta/status')
        .then((r) => r.json())
        .then(setStatus)
        .catch(() => setStatus({ api: 'DOWN', database: 'UNKNOWN', databaseLatencyMs: -1, syncQueue: {}, sources: [] }))
    }
    load()
    const timer = setInterval(load, 30_000)
    return () => clearInterval(timer)
  }, [])

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <header className="flex items-center gap-2">
        <ShieldCheck className="h-6 w-6 text-brand-600" aria-hidden />
        <h1 className="text-xl font-semibold">{t('status.title', 'VBAnnagate status')}</h1>
      </header>

      <div className="card flex items-center justify-between p-5">
        <div>
          <div className="text-xs uppercase tracking-wide text-[var(--text-muted)]">API</div>
          <div className="text-sm text-[var(--text-muted)]">
            {status ? t('status.latency', { ms: status.databaseLatencyMs }) : t('status.checking', 'checking…')}
          </div>
        </div>
        <Badge state={status?.api ?? 'UNKNOWN'} />
      </div>

      <div className="card flex items-center justify-between p-5">
        <div>
          <div className="text-xs uppercase tracking-wide text-[var(--text-muted)]">{t('status.database', 'Serving database')}</div>
        </div>
        <Badge state={status?.database ?? 'UNKNOWN'} />
      </div>

      <div className="card p-5">
        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
          {t('status.freshness', 'Registry source freshness')}
        </div>
        <div className="space-y-2 text-sm">
          {(status?.sources ?? []).map((source) => (
            <div key={source.source_id} className="flex items-center gap-3">
              <Badge state={source.freshness} />
              <span className="font-medium">{source.source_id}</span>
              <span className="text-[var(--text-muted)]">
                {source.country_code ?? '*'} · {source.record_count.toLocaleString()} {t('status.records', 'records')}
              </span>
              <span className="ml-auto text-xs tabular-nums text-[var(--text-muted)]">
                {source.last_sync_at?.slice(0, 16).replace('T', ' ') ?? t('status.never', 'never')}
              </span>
            </div>
          ))}
          {(status?.sources.length ?? 0) === 0 && (
            <div className="text-[var(--text-muted)]">{t('status.noSources', 'No enabled sources.')}</div>
          )}
        </div>
      </div>

      <p className="text-xs text-[var(--text-muted)]">{t('status.autoRefresh', 'Auto-refreshes every 30 seconds.')}</p>
    </div>
  )
}
